// Penalty Kings 3D: bootstraps the UI, settings, audio, start.ng identity and the game.
import { Game } from './game/game.js';
import { UI } from './ui/ui.js';
import { GameAudio } from './audio.js';
import { Input } from './input.js';
import { loadSettings, saveSettings, loadBest, recordBest, stashPendingRun, takePendingRun } from './storage.js';
import {
  StartNgPlayer, fetchLeaderboard, submitRun, signInUrl, canSignIn, cleanSessionFromUrl,
} from './startng.js';

const params = new URLSearchParams(location.search);
const TEST = params.has('test');
const COVER = params.has('cover');

const ui = new UI();
const settings = loadSettings();
const audio = new GameAudio();
const player = new StartNgPlayer();
let game;
let lastRun = null; // { run, stats } of the most recent finished run

cleanSessionFromUrl();

function applySettings() {
  audio.setVolume(settings.volume);
  audio.setMuted(!settings.sound);
  audio.setCrowd(settings.crowd);
  ui.setMuted(!settings.sound);
  document.body.classList.toggle('reduced-motion', settings.reducedMotion);
  saveSettings(settings);
}

function bindSettings() {
  const vol = document.getElementById('set-volume');
  const map = { 'set-sound': 'sound', 'set-crowd': 'crowd', 'set-preview': 'preview', 'set-replays': 'replays', 'set-motion': 'reducedMotion' };
  vol.value = String(Math.round(settings.volume * 100));
  vol.addEventListener('input', () => { settings.volume = Number(vol.value) / 100; applySettings(); });
  for (const [id, key] of Object.entries(map)) {
    const el = document.getElementById(id);
    el.checked = !!settings[key];
    el.addEventListener('change', () => { settings[key] = el.checked; applySettings(); audio.click(); });
  }
  const q = document.getElementById('set-quality');
  q.value = settings.quality;
  q.addEventListener('change', () => { settings.quality = q.value; game.adaptiveCap = undefined; game.forcedLow = false; game.applyQuality(); applySettings(); });
}

function toggleMute() {
  audio.unlock();
  settings.sound = !settings.sound;
  document.getElementById('set-sound').checked = settings.sound;
  applySettings();
}

// ---------------------------------------------------------------- start.ng

function renderProfile() { ui.setProfile(player.profile, player.status); }

function signIn(pendingStats) {
  if (!canSignIn()) {
    ui.toast('Sign-in works when the game is played on start.ng.');
    return;
  }
  if (pendingStats) stashPendingRun(pendingStats);
  location.href = signInUrl();
}

async function openLeaderboard() {
  ui.openModal('modal-board');
  ui.boardLoading();
  const res = await fetchLeaderboard(10);
  if (!ui.isOpen('modal-board')) return;
  if (!res.ok) {
    ui.boardMessage(res.error, { label: 'Try again', onClick: openLeaderboard });
    return;
  }
  const meId = player.user?.id ?? null;
  if (!res.entries.length) {
    ui.boardMessage('No scores yet. Finish a run while signed in to be the first on the board!',
      player.user ? null : { label: 'Sign in with start.ng', onClick: () => signIn() });
    return;
  }
  const mine = meId != null && res.entries.some((e) => e.player_id === meId);
  const footer = player.user
    ? (mine ? '' : 'You are not in the top 10 yet.')
    : 'Guests can view the board. Sign in with start.ng to save your runs.';
  ui.boardTable(res.entries, meId, footer);
}

async function saveRun(stats) {
  if (player.status === 'offline' || player.status === 'loading') {
    // ask once more in case start.ng was just slow
    ui.setSaveStatus({ kind: 'saving', text: 'Connecting to start.ng…' });
    await player.refresh({ force: true });
  }
  if (!player.user) {
    if (player.status === 'offline') {
      ui.setSaveStatus({ kind: 'offline', text: 'Shared leaderboard unavailable here. Runs are saved when you play on start.ng. Your local best is kept.' });
    } else {
      ui.setSaveStatus({ kind: 'guest', text: 'Sign in with start.ng to save this run to the shared leaderboard.', action: { label: 'Sign in to save', onClick: () => signIn(stats) } });
    }
    return;
  }
  ui.setSaveStatus({ kind: 'saving', text: 'Saving to the start.ng leaderboard…' });
  const res = await submitRun(stats);
  if (res.ok) {
    const r = res.result;
    let text = 'Saved to the start.ng leaderboard.';
    if (r && typeof r.rank === 'number') {
      text = r.improved ? `Saved: new best! You're #${r.rank} on the leaderboard.` : `Saved. Your best is still ${Number(r.best).toLocaleString('en-US')} pts (#${r.rank}).`;
    }
    ui.setSaveStatus({ kind: 'ok', text });
  } else {
    const expired = /sign in/i.test(res.error || '');
    if (expired) {
      await player.refresh({ force: true });
      ui.setSaveStatus({ kind: 'err', text: `${res.error}`, action: { label: 'Sign in to save', onClick: () => signIn(stats) } });
    } else {
      ui.setSaveStatus({ kind: 'err', text: res.error, action: { label: 'Retry', onClick: () => saveRun(stats) } });
    }
  }
}

/** A run finished before a sign-in redirect: save it now that the player is back and signed in. */
async function savePendingRun() {
  if (!player.user) return;
  const stats = takePendingRun();
  if (!stats) return;
  const res = await submitRun(stats);
  if (res.ok) {
    const r = res.result;
    ui.toast(r?.rank ? `Your last run was saved: ${r.improved ? 'new best, ' : ''}#${r.rank} on the leaderboard.` : 'Your last run was saved to the leaderboard.', 5000);
  } else {
    ui.toast(`Your last run could not be saved: ${res.error}`, 5000);
  }
}

// ---------------------------------------------------------------- flow

function onRunOver(run, stats) {
  lastRun = { run, stats };
  const isNewBest = recordBest(stats);
  const best = loadBest();
  ui.setBest(best);
  ui.showHud(false);
  ui.showGameOver(run, stats, best, isNewBest);
  saveRun(stats);
}

function play() {
  audio.unlock();
  audio.click();
  for (const id of ['modal-over', 'modal-pause', 'modal-board', 'modal-howto', 'modal-settings']) ui.closeModal(id);
  ui.showMenu(false);
  ui.showHud(true);
  game.startRun();
  game.renderer.domElement.focus({ preventScroll: true });
}

function pauseToggle() {
  if (!game || game.state === 'menu' || game.state === 'over' || game.state === 'boot') {
    ui.closeTopModal();
    return;
  }
  if (game.paused) {
    if (ui.isOpen('modal-settings')) { ui.closeModal('modal-settings'); return; }
    resume();
  } else {
    game.setPaused(true);
    ui.openModal('modal-pause');
  }
}

function resume() {
  ui.closeModal('modal-pause');
  ui.closeModal('modal-settings');
  game.setPaused(false);
}

function toMenu() {
  for (const id of ['modal-over', 'modal-pause', 'modal-settings']) ui.closeModal(id);
  ui.showHud(false);
  ui.showMenu(true);
  game.toMenu();
}

function wireButtons() {
  const on = (id, fn) => document.getElementById(id).addEventListener('click', fn);
  on('btn-play', play);
  on('btn-again', play);
  on('btn-howto', () => { audio.unlock(); ui.openModal('modal-howto'); });
  on('btn-board', () => { audio.unlock(); openLeaderboard(); });
  on('btn-over-board', openLeaderboard);
  on('board-refresh', openLeaderboard);
  on('btn-settings', () => { audio.unlock(); ui.openModal('modal-settings'); });
  on('btn-pause-settings', () => ui.openModal('modal-settings'));
  on('btn-pause', pauseToggle);
  on('btn-resume', resume);
  on('btn-restart', () => { ui.closeModal('modal-pause'); play(); });
  on('btn-quit', toMenu);
  on('btn-over-menu', toMenu);
  on('btn-mute', toggleMute);
  on('replay-badge', () => game.skip());
  on('player-sign-in', () => signIn(game.state === 'over' && lastRun ? lastRun.stats : null));
  document.getElementById('curve').addEventListener('click', (e) => {
    const b = e.target.closest('button[data-curve]');
    if (b) game.setCurve(Number(b.dataset.curve));
  });
  // a click on the scene skips intro banners and replays
  game.renderer.domElement.addEventListener('click', () => game.skip());
}

function onVisibility() {
  const hidden = document.hidden;
  audio.setHidden(hidden);
  if (hidden && game && !game.paused && ['aim', 'charge', 'flight', 'result', 'intro', 'ready', 'replay', 'cleared'].includes(game.state)) {
    game.setPaused(true);
    ui.openModal('modal-pause');
  }
}

// ---------------------------------------------------------------- boot

async function boot() {
  applySettings();
  ui.setBest(loadBest());
  renderProfile();
  player.onChange(renderProfile);

  ui.setLoading('Building the stadium…');
  await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0))); // let the loader paint
  try {
    game = new Game({ container: document.getElementById('stage'), audio, ui, settings, onRunOver });
    game.init();
  } catch (err) {
    console.error(err);
    ui.fatal(err?.message || 'The 3D view could not be started.');
    return;
  }
  // compile every shader up front (in parallel where supported) so the first frame doesn't stall
  ui.setLoading('Warming up the floodlights…');
  try {
    await game.renderer.compileAsync(game.scene, game.camera);
  } catch (err) {
    console.warn('Shader pre-compilation skipped:', err);
  }
  game.input = new Input(game.renderer.domElement, document.getElementById('btn-shoot'), {
    gesture: () => audio.unlock(),
    aimAt: (x, y) => game.aimAt(x, y),
    aimBy: (dx, dy) => game.aimBy(dx, dy),
    chargeStart: () => game.chargeStart(),
    chargeEnd: () => game.chargeEnd(),
    chargeCancel: () => game.chargeCancel(),
    curveStep: (d) => game.curveStep(d),
    pause: pauseToggle,
    mute: toggleMute,
    confirm: () => {
      if (game.state === 'menu' && !ui.anyModal()) play();
      else if (game.state === 'over') play();
      else game.skip();
    },
    touchMode: (t) => { game.setTouch(t); ui.setControlsHint(t); },
  });
  ui.setControlsHint(game.input.touch);
  game.setTouch(game.input.touch);
  bindSettings();
  wireButtons();
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', () => audio.setHidden(true));

  if (!COVER) ui.showMenu(true); // under the loading screen until the first frames are drawn
  game.start();
  requestAnimationFrame(() => requestAnimationFrame(() => ui.hideLoading()));

  if (COVER) setupCover();

  // start.ng identity (never blocks the game)
  player.watch();
  player.refresh({ force: true }).then(savePendingRun);

  if (TEST || COVER) {
    window.__pk = { game, ui, player, audio, settings, lastRun: () => lastRun, play, pauseToggle, toMenu, openLeaderboard };
  }
}

/** ?cover: a staged frame used to render cover.png. */
function setupCover() {
  document.body.classList.add('cover-mode');
  for (const id of ['player-card', 'menu']) document.getElementById(id).hidden = true;
  document.querySelector('.top-actions').hidden = true;
  game.settings.preview = false;
  game.settings.replays = false;
  game.startRun();
  ui.hideBanner();
  ui.showHud(false);
  game.state = 'aim';
  game.aim.set(2.9, 2.0);
  game.setCurve(0.5);
  game.time = 0;
  game.kick(0.62);
  window.__coverFreeze = (t) => new Promise((resolve) => {
    const check = () => {
      if (game.shot && game.shot.t >= t) { game.timeScale = 0; game.slowmo = 0; game.paused = true; resolve(); } else requestAnimationFrame(check);
    };
    check();
  });
}

boot();
